import React from 'react';
import { Modal, Row, Col, Badge, Button, Card } from 'react-bootstrap';
import { FaEye, FaQuestionCircle, FaTimes } from 'react-icons/fa';

const GenericViewModal = ({ show, onHide, item, title, fields, showStatus = true }) => {
  // Early return if modal shouldn't show or no item
  if (!show || !item) return null;

  // Safely handle fields array
  const safeFields = Array.isArray(fields) ? fields : [];

  // Helper function to determine status based on different possible field names
  const getItemStatus = () => {
    if (typeof item.exIsActive !== 'undefined') {
      return item.exIsActive === 1 || item.exIsActive === '1' || item.exIsActive === true;
    }
    if (typeof item.isActive !== 'undefined') {
      return item.isActive === 1 || item.isActive === '1' || item.isActive === true;
    }
    if (typeof item.status !== 'undefined') {
      return item.status === 1 || item.status === '1' || item.status === true || item.status === 'active';
    }
    return null; // Unknown status
  };

  const isActive = getItemStatus();

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered 
      backdrop="static"
      className="professional-view-modal"
    >
      {/* Clean Professional Header */}
      <Modal.Header className="border-0 pb-0">
        <div className="w-100 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div className="me-3 p-2 bg-light rounded">
              <FaEye className="text-primary" size={20} />
            </div>
            <div>
              <h4 className="mb-0 fw-semibold text-dark">{title || 'Item'} Details</h4>
              <small className="text-muted">Detailed information view</small>
            </div>
          </div>
          <Button 
            variant="outline-secondary" 
            onClick={onHide}
            size="sm"
            className="rounded-circle p-2"
          >
            <FaTimes size={14} />
          </Button>
        </div>
      </Modal.Header>
      
      <Modal.Body className="px-4 py-3">
        {safeFields.length === 0 ? (
          <div className="text-center py-5">
            <FaQuestionCircle size={48} className="text-muted mb-3" />
            <h5 className="text-muted mb-2">No Details Available</h5>
            <p className="text-secondary">There is no information to display for this item.</p>
          </div>
        ) : (
          <>
            {/* Status Section */}
            {showStatus && isActive !== null && (
              <div className="mb-4 p-3 bg-light rounded border">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="mb-1 fw-semibold text-dark">Status</h6>
                    <small className="text-muted">Current operational status</small>
                  </div>
                  <Badge 
                    bg={isActive ? 'success' : 'secondary'}
                    className="px-3 py-2 fw-normal"
                  >
                    {isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            )}

            {/* Information Grid */}
            <Row className="g-3">
              {safeFields.map((field, index) => {
                // Handle cases where field might not have expected structure
                if (!field || typeof field !== 'object') {
                  console.warn('Invalid field structure:', field);
                  return null;
                }

                const {
                  label = `Field ${index}`,
                  value = '-',
                  icon: Icon = FaQuestionCircle,
                  fullWidth = false,
                  render // Allow custom render function
                } = field;

                // Use custom render function if provided
                const displayValue = render && typeof render === 'function' 
                  ? render(item) 
                  : value;

                return (
                  <Col lg={fullWidth ? 12 : 6} key={`${label}-${index}`}>
                    <div className="border rounded p-3 bg-white h-100">
                      <div className="d-flex align-items-start mb-2">
                        <div className="me-2 mt-1">
                          {Icon && <Icon className="text-primary" size={16} />}
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="mb-1 fw-semibold text-dark small text-uppercase tracking-wide">
                            {label}
                          </h6>
                          <div className="text-secondary">
                            {displayValue || <span className="text-muted fst-italic">Not specified</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </>
        )}
      </Modal.Body>
      
      <Modal.Footer className="border-0 pt-0">
        <Button 
          variant="secondary" 
          onClick={onHide}
          className="px-4"
        >
          Close
        </Button>
      </Modal.Footer>

      {/* Professional Styling */}
      <style jsx>{`
        .professional-view-modal .modal-content {
          border: 1px solid #dee2e6;
          border-radius: 8px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
        }

        .professional-view-modal .modal-header {
          background-color: #ffffff;
          padding: 1.5rem 1.5rem 0.75rem;
        }

        .professional-view-modal .modal-body {
          background-color: #fafbfc;
          min-height: 400px;
        }

        .professional-view-modal .modal-footer {
          background-color: #ffffff;
          padding: 0.75rem 1.5rem 1.5rem;
        }

        .tracking-wide {
          letter-spacing: 0.025em;
        }

        .professional-view-modal .border {
          border-color: #e9ecef !important;
        }

        .professional-view-modal .bg-light {
          background-color: #f8f9fa !important;
        }

        .professional-view-modal .text-primary {
          color: #0066cc !important;
        }

        .professional-view-modal .btn-outline-secondary {
          border-color: #6c757d;
          color: #6c757d;
        }

        .professional-view-modal .btn-outline-secondary:hover {
          background-color: #6c757d;
          border-color: #6c757d;
        }

        .professional-view-modal .btn-secondary {
          background-color: #6c757d;
          border-color: #6c757d;
        }

        .professional-view-modal .btn-secondary:hover {
          background-color: #5a6268;
          border-color: #545b62;
        }

        .professional-view-modal .badge {
          font-size: 0.875rem;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .professional-view-modal .modal-body {
            padding: 1rem;
          }
          
          .professional-view-modal .modal-header {
            padding: 1rem 1rem 0.5rem;
          }
          
          .professional-view-modal .modal-footer {
            padding: 0.5rem 1rem 1rem;
          }
        }
      `}</style>
    </Modal>
  );
};

export default GenericViewModal;